// Tronçon selection: a "tronçon" is one built infrastructure row (server id).
// In the infra tool's *Sélection / Édition* mode the overlay stops being
// coloured by infra *type* and is coloured by *identity* instead — that is the
// whole point of the mode: telling two adjacent routes apart is impossible when
// both are the same "Route" orange, and merging the wrong pair is not undoable.
//
// Once something is selected the mode narrows to that type: only same-type
// tronçons keep a colour, everything else is dimmed. Merging is only defined
// within one type, so showing candidates that can never be merged is noise.

// Deliberately high-contrast and stable: neighbours in the list never share a
// hue family, so consecutive ids stay distinguishable.
export const TRONC_COLORS = [
  '#FF3B30', '#34C759', '#0A84FF', '#FFD60A', '#FF2D95', '#5AC8FA',
  '#FF9F0A', '#BF5AF2', '#00C7BE', '#FF6B6B', '#A3E635', '#F472B6',
]

/** Colour of a dimmed (out-of-focus) tronçon. */
export const TRONC_DIM_COLOR = '#6B6B6B'

export const troncColor = (infraId, index) => {
  const i = Number.isInteger(index) ? index : Number(infraId) || 0
  return TRONC_COLORS[((i % TRONC_COLORS.length) + TRONC_COLORS.length) % TRONC_COLORS.length]
}

/**
 * MapLibre `match` expression colouring each tronçon by identity. Ids are
 * matched in list order so the palette follows the list the user sees in the
 * sidebar, not the raw id values.
 */
export const troncColorExpression = (infrastructures, fallback = '#FF8C00') => {
  const ids = (infrastructures || []).map((i) => i.id).filter((id) => id != null)
  if (ids.length === 0) return fallback
  return ['match', ['get', 'id'], ...ids.flatMap((id, index) => [id, troncColor(id, index)]), fallback]
}

/** The type the selection is locked onto — the first selected tronçon's. */
export const troncFocusType = (selectedIds, infrastructures) => {
  const first = (selectedIds || [])[0]
  if (first == null) return null
  const row = (infrastructures || []).find((i) => i.id === first)
  return row ? row.type : null
}

/** Tronçons eligible for the current selection (all of them until one is picked). */
export const focusedTroncons = (infrastructures, focusType) => {
  const all = infrastructures || []
  return focusType ? all.filter((i) => i.type === focusType) : all
}

/**
 * Click on a tronçon in selection mode. Clicking one of another type does not
 * silently do nothing (the merge would be refused later anyway) — it restarts
 * the selection on that type, which is what the click was asking for.
 *
 * Returns `{ ids, retyped }`; `retyped` is the new focus type when the click
 * switched types, so the caller can say so.
 */
export const toggleTroncSelection = (selectedIds, infrastructures, clickedId) => {
  const ids = selectedIds || []
  const all = infrastructures || []
  const clicked = all.find((i) => i.id === clickedId)
  if (!clicked) return { ids, retyped: null }

  const focusType = troncFocusType(ids, all)
  if (focusType && clicked.type !== focusType) {
    return { ids: [clickedId], retyped: clicked.type }
  }
  return {
    ids: ids.includes(clickedId) ? ids.filter((id) => id !== clickedId) : [...ids, clickedId],
    retyped: null,
  }
}

/** Merging is only defined for routes of one type — the survivor keeps it. */
export const troncMergeBlocker = (selected) => {
  if (!selected || selected.length < 2) return 'Sélectionnez au moins deux tronçons.'
  const types = new Set(selected.map((t) => t.type))
  if (types.size > 1) return `Types différents (${[...types].join(', ')}) — la fusion exige un seul type.`
  return null
}
